import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { Clock, Book, ArrowRight } from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Guides() {
  usePageTitle('Expert Guides');
  const [guides, setGuides] = useState([]);
  const [filteredGuides, setFilteredGuides] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');

  useEffect(() => {
    fetchGuides();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      setFilteredGuides(guides.filter((g) => g.category === selectedCategory));
    } else {
      setFilteredGuides(guides);
    }
  }, [selectedCategory, guides]);

  const fetchGuides = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/guides`);
      setGuides(response.data);
      setFilteredGuides(response.data);
    } catch (error) {
      console.error('Error fetching guides:', error);
    }
  };

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto pb-24">
      {/* Header */}
      <div className="mb-16">
        <h1
          className="text-5xl md:text-6xl font-medium tracking-tight mb-4"
          style={{ fontFamily: 'Playfair Display, serif' }}
          data-testid="guides-title"
        >
          Expert <span className="text-gold">Guides</span>
        </h1>
        <p className="text-lg text-white/60 max-w-2xl">
          Magazine-quality insights on superyacht ownership, chartering, and Southeast Asian cruising from our team of maritime experts.
        </p>
      </div>

      {/* Knowledge Hub Banner */}
      <Link 
        to="/guides/essentials" 
        className="block mb-16 glass-heavy p-8 md:p-12 hover:bg-white/5 transition-colors group"
        data-testid="knowledge-hub-banner"
      >
        <div className="flex flex-col md:flex-row items-center gap-8">
          <div className="w-20 h-20 bg-gold/20 rounded-full flex items-center justify-center shrink-0">
            <Book size={40} className="text-gold" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <p className="text-xs uppercase tracking-[0.2em] text-gold mb-2">Complete Knowledge Hub</p>
            <h2 className="text-3xl md:text-4xl font-medium mb-3 group-hover:text-gold transition-colors" style={{ fontFamily: 'Playfair Display, serif' }}>
              Yacht Essentials: From Basics to Beyond
            </h2>
            <p className="text-white/70">
              Your comprehensive A-to-Z guide to understanding yachts. Learn everything from terminology to ownership, propulsion types to hull design.
            </p>
          </div>
          <Button className="bg-gold text-[#0A192F] hover:bg-[#E5C55D] shrink-0">
            Explore <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>
      </Link>

      {/* Filter */}
      <div className="mb-12">
        <h3 className="text-xl font-medium mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
          Featured Articles
        </h3>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-full md:w-64 bg-transparent border-white/20 text-white" data-testid="category-filter">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent className="bg-[#0A192F] border-white/20 text-white">
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="destinations">Destinations</SelectItem>
            <SelectItem value="types">Yacht Types</SelectItem>
            <SelectItem value="buying">Buying Guide</SelectItem>
            <SelectItem value="chartering">Chartering</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Guides Grid */}
      <div className="space-y-16" data-testid="guides-grid">
        {filteredGuides.map((guide, idx) => (
          <Link
            key={guide.guide_id}
            to={`/guide/${guide.guide_id}`}
            className={`group grid grid-cols-1 ${idx % 2 === 0 ? 'lg:grid-cols-12' : 'lg:grid-cols-12'} gap-12 items-center`}
            data-testid={`guide-card-${guide.guide_id}`}
          >
            <div className={`${idx % 2 === 0 ? 'lg:col-span-7' : 'lg:col-span-7 lg:order-2'} aspect-[16/10] overflow-hidden`}>
              <img
                src={guide.image_url}
                alt={guide.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
            </div>
            <div className={`${idx % 2 === 0 ? 'lg:col-span-5' : 'lg:col-span-5 lg:order-1'}`}>
              <p className="text-xs uppercase tracking-[0.2em] text-gold mb-4">{guide.category}</p>
              <h2
                className="text-3xl md:text-4xl font-medium mb-6 group-hover:text-gold transition-colors"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {guide.title}
              </h2>
              <p className="text-white/70 leading-relaxed mb-6">{guide.summary}</p>
              <div className="flex items-center gap-2 text-white/40 text-sm">
                <Clock size={14} />
                <span>{guide.read_time_minutes} min read</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
